'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
  onSubmitOrder: (orderDetails: OrderDetails) => void;
}

export interface OrderDetails {
  orderType: 'adhoc' | 'wholesale';
  deliveryMethod: 'pickup' | 'delivery';
  pickupTime?: string;
  deliveryAddress?: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
}

export default function CheckoutModal({
  isOpen,
  onClose,
  total,
  onSubmitOrder,
}: CheckoutModalProps) {
  const [orderType, setOrderType] = useState<'adhoc' | 'wholesale'>('adhoc');
  const [deliveryMethod, setDeliveryMethod] = useState<'pickup' | 'delivery'>('pickup');
  const [pickupTime, setPickupTime] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmitOrder({
      orderType,
      deliveryMethod,
      pickupTime: deliveryMethod === 'pickup' ? pickupTime : undefined,
      deliveryAddress: deliveryMethod === 'delivery' ? deliveryAddress : undefined,
      customerName,
      customerPhone,
      customerEmail,
    });
  };

  const timeSlots = [
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-black text-foreground font-heading">
            Complete Your Order
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            aria-label="Close checkout"
          >
            <Icon name="XIcon" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Order Type */}
          <div>
            <label className="block text-sm font-bold text-foreground mb-3">
              Order Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setOrderType('adhoc')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  orderType === 'adhoc' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <Icon name="ShoppingBagIcon" size={24} className="text-primary mx-auto mb-2" />
                <p className="font-bold text-foreground">Ad-Hoc Order</p>
                <p className="text-xs text-muted-foreground mt-1">Individual items</p>
              </button>
              <button
                type="button"
                onClick={() => setOrderType('wholesale')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  orderType === 'wholesale' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <Icon name="TruckIcon" size={24} className="text-primary mx-auto mb-2" />
                <p className="font-bold text-foreground">Wholesale</p>
                <p className="text-xs text-muted-foreground mt-1">Bulk orders</p>
              </button>
            </div>
          </div>

          {/* Delivery Method */}
          <div>
            <label className="block text-sm font-bold text-foreground mb-3">
              Delivery Method
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setDeliveryMethod('pickup')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  deliveryMethod === 'pickup' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <Icon name="ClockIcon" size={24} className="text-primary mx-auto mb-2" />
                <p className="font-bold text-foreground">Pickup</p>
                <p className="text-xs text-muted-foreground mt-1">Choose time slot</p>
              </button>
              <button
                type="button"
                onClick={() => setDeliveryMethod('delivery')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  deliveryMethod === 'delivery' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <Icon name="TruckIcon" size={24} className="text-primary mx-auto mb-2" />
                <p className="font-bold text-foreground">Delivery</p>
                <p className="text-xs text-muted-foreground mt-1">Enter address</p>
              </button>
            </div>
          </div>

          {/* Pickup Time Slots */}
          {deliveryMethod === 'pickup' && (
            <div>
              <label className="block text-sm font-bold text-foreground mb-3">
                Select Pickup Time
              </label>
              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-accent rounded-xl">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setPickupTime(time)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                      pickupTime === time
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-foreground hover:bg-primary/20'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Delivery Address */}
          {deliveryMethod === 'delivery' && (
            <div>
              <label htmlFor="deliveryAddress" className="block text-sm font-bold text-foreground mb-2">
                Delivery Address
              </label>
              <textarea
                id="deliveryAddress"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                required
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none bg-input text-foreground"
                placeholder="Enter your delivery address..."
              />
            </div>
          )}

          {/* Customer Information */}
          <div className="space-y-4">
            <div>
              <label htmlFor="customerName" className="block text-sm font-bold text-foreground mb-2">
                Full Name
              </label>
              <input
                id="customerName"
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none bg-input text-foreground"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="customerPhone" className="block text-sm font-bold text-foreground mb-2">
                Phone Number
              </label>
              <input
                id="customerPhone"
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none bg-input text-foreground"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="customerEmail" className="block text-sm font-bold text-foreground mb-2">
                Email Address
              </label>
              <input
                id="customerEmail"
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none bg-input text-foreground"
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Order Total */}
          <div className="bg-accent rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Order Total</span>
              <span className="text-3xl font-black text-primary">${total.toFixed(2)}</span>
            </div>
            {orderType === 'wholesale' && (
              <p className="text-xs text-muted-foreground mt-2">
                Wholesale discount applied
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-success text-success-foreground py-4 rounded-full font-bold uppercase tracking-wider hover:bg-success/90 transition-all hover:scale-105 shadow-lg flex items-center justify-center gap-2"
          >
            <Icon name="CheckCircleIcon" size={20} />
            <span>Place Order</span>
          </button>
        </form>
      </div>
    </div>
  );
}